import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The global error boundary (Phase 7 task list).
 *
 * A class component because React still offers no hook equivalent. It catches render-time
 * failures only — a rejected fetch is TanStack Query's job, and the pages handle that with
 * `<ErrorState>`; this is the net under a component that threw while rendering, so one bad
 * card cannot blank the whole publication.
 *
 * On the server this never renders: SSR failures fail soft in `apps/api/src/ssr.ts`, which
 * serves the shell with correct meta tags rather than a 500 (§1.10).
 */
interface Props {
  children: ReactNode;
  fallback: (reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  private readonly reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) return this.props.fallback(this.reset);
    return this.props.children;
  }
}
